import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { societyFacilityCreateSchema } from '../../../../forms/schemas/society_facility_create'
import { NFacilities } from '../../../../interfaces/services/facilities.interfaces'
import { ToastType } from '../../../../components/common/Toast/toast-type'
// eslint-disable-next-line max-len
import { CreateUpdateFacilityForm } from '../../../../components/society/FacilitiesList/CreateUpdateFacilityForm'
import { ViewBase } from '../../../../components/common/ViewBase'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../../navigation/reverseDocumentTitle'
import {
  createFacility,
  facilityQueries,
  patchFacility,
} from '../../../../api/facilities'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'

export const CreateUpdateFacilityView = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { facilityId } = useParams()
  const navigate = useNavigate()
  const { data: facility, isLoading: isLoadingFacility } = useQuery(
    facilityQueries.getFacility(facilityId)
  )
  const { society, isLoading: isLoadingSociety } = useCurrentSociety()

  const updateMode = !!facility

  useDocumentTitle(
    updateMode
      ? reverseDocumentTitle('society:facilities-update', {
          '{{ societyName }}': society?.name,
          '{{ facilityName }}': facility?.name,
        })
      : reverseDocumentTitle('society:facilities-create', {
          '{{ societyName }}': society?.name,
        })
  )

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateFacility.flashMessage.updateFacilityFailure'
      : 'createUpdateFacility.flashMessage.createFacilityFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateFacility.flashMessage.updateFacilitySuccess'
      : 'createUpdateFacility.flashMessage.createFacilitySuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const goBack = (): void => {
    navigate(-1)
  }

  const { mutate: mutatePatchFacility, isPending: isPatchingFacility } =
    useMutation({
      mutationFn: patchFacility,
      onSuccess: () => {
        showSuccessMessage()
        goBack()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const { mutate: mutateCreateFacility, isPending: isCreatingFacility } =
    useMutation({
      mutationFn: createFacility,
      onSuccess: () => {
        showSuccessMessage()
        goBack()
      },
      onError: () => {
        showErrorMessage()
      },
    })
  const onSubmit = async (
    data: InferType<typeof societyFacilityCreateSchema>
  ): Promise<void> => {
    const bookingAvailabilityStartDate = data.bookingAvailabilityStartDate
      ? new Date(data.bookingAvailabilityStartDate)
      : null
    const bookingAvailabilityEndDate = data.bookingAvailabilityEndDate
      ? new Date(data.bookingAvailabilityEndDate)
      : null

    const getBookingAvailabilityEndDate = (): null | Date => {
      if (data.bookableUntilFurtherNotice) {
        return null
      }
      return bookingAvailabilityEndDate
        ? new Date(bookingAvailabilityEndDate.setUTCHours(0, 0, 0, 0))
        : null
    }

    const formData: NFacilities.NCreate.IRequestBody = {
      ...data,
      bookingAvailability: [
        {
          ...(bookingAvailabilityStartDate && {
            startDate: new Date(
              bookingAvailabilityStartDate?.setUTCHours(0, 0, 0, 0)
            ),
          }),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          endDate: getBookingAvailabilityEndDate(),
        },
      ],
    }

    if (updateMode) {
      mutatePatchFacility({ id: facility._id, body: formData })
    } else {
      mutateCreateFacility(formData)
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading =
    isLoadingSociety ||
    isLoadingFacility ||
    isPatchingFacility ||
    isCreatingFacility

  const isDataReady = (): boolean => {
    if (!society) return false
    if (facilityId && !facility) return false
    return true
  }

  return (
    <ViewBase
      title={translate(
        updateMode
          ? 'createUpdateFacility.editFacilityTitle'
          : 'createUpdateFacility.createFacilityTitle'
      )}
      showBackButton
    >
      {isDataReady() && society && (
        <CreateUpdateFacilityForm
          society={society}
          onError={onError}
          onSubmit={onSubmit}
          onClose={goBack}
          loading={loading}
          facility={facility}
        />
      )}
    </ViewBase>
  )
}
